import TeamRoleStore from 'iam/TeamRoleStore'
import TeamMemberStore from 'iam/TeamMemberStore'
import { GetCurrentMember } from 'iam/SessionStore'
import Team from 'iam/models/Team'
import InvitationStore from 'iam/InvitationStore'
import { task } from 'mobx-task'
import { Id } from 'types/definitions'
import { computed, action, observable, makeObservable } from 'mobx'
import TeamMember from 'iam/models/TeamMember'
import { TableSortOrder } from 'components/Table'
import orderBy from 'lodash/orderBy'
import { ConfirmViewModel } from 'components/ConfirmDialog/ConfirmViewModel'
import { AlertFactory, AlertState } from 'alerts/Alert'
import InviteTeamMemberModalViewModel from 'iam/components/InviteTeamMemberModal/InviteTeamMemberModalViewModel'
import PendingTeamMembersViewModel from './PendingTeamMembersViewModel'

export type TeamMemberKind = 'Current' | 'Pending'
export type TeamMemberTableColumns = {
  header: string
  key: 'member.displayName' | 'teamRole.name' | 'dateCreated'
  sortable?: boolean
}

export const teamMemberTableColumns: TeamMemberTableColumns[] = [
  {
    header: 'Name',
    key: 'member.displayName',
    sortable: true,
  },
  {
    header: 'Role',
    key: 'teamRole.name',
    sortable: false,
  },
  {
    header: 'Date Joined',
    key: 'dateCreated',
    sortable: true,
  },
]

export default class TeamMembersViewModel {
  @observable searchText = ''
  @observable sortOrder: TableSortOrder = 'none'
  @observable sortField: TeamMemberTableColumns['key'] = 'member.displayName'
  @observable visibleKind: TeamMemberKind = 'Current'

  onInvitationSent = async () => {
    await this.teamMemberStore.findPendingMembersForTeam({
      team_id: Id.assert(this.team.id),
      workspace_id: Id.assert(this.team.workspace.id),
    })

    this.setVisibleKind('Pending')
  }

  inviteTeamMemberModalVM = new InviteTeamMemberModalViewModel(
    this.invitationStore,
    this.teamRoleStore,
    this.team,
    () => this.teamMembers,
    this.onInvitationSent
  )

  removeConfirmDialog: ConfirmViewModel = new ConfirmViewModel()

  pendingMembersVM = new PendingTeamMembersViewModel(
    this.team,
    () => this.pendingTeamMembers,
    () => this.searchText
  )

  constructor(
    public team: Team,
    private teamRoleStore: TeamRoleStore,
    private teamMemberStore: TeamMemberStore,
    private invitationStore: InvitationStore,
    private getCurrentMember: GetCurrentMember,
    private newAlert: AlertFactory
  ) {
    makeObservable(this)
  }

  activate = task(async () => {
    if (!this.canManageTeamMember) {
      return
    }
    await Promise.all([
      this.teamRoleStore.fetchTeamRolesForWorkspace(
        Id.assert(this.team.workspace.id)
      ),
      this.teamMemberStore.find({
        team_id: Id.assert(this.team.id),
      }),
      this.teamMemberStore.findPendingMembersForTeam({
        team_id: Id.assert(this.team.id),
        workspace_id: Id.assert(this.team.workspace.id),
      }),
    ])
  })

  @action.bound
  setSearchText(query: string) {
    this.searchText = query
  }

  /**
   *
   * Whether to show Current Members or Pending Members
   */
  @action.bound
  setVisibleKind(memberKind: TeamMemberKind) {
    this.visibleKind = memberKind
  }

  /**
   * Team members.
   */
  @computed
  get teamMembers() {
    return this.teamMemberStore.forTeam(Id.assert(this.team.id))
  }

  @computed
  get pendingTeamMembers() {
    return this.teamMemberStore.pendingForTeam(Id.assert(this.team.id))
  }

  @computed
  get hasPendingMembers() {
    return this.pendingTeamMembers.length > 0
  }

  @computed
  get currentSort() {
    return this.sortField
  }

  @computed
  get currentSortOrder() {
    return this.sortOrder
  }

  @computed
  get filtered() {
    let teamMembers = this.teamMembers

    if (this.searchText !== '') {
      teamMembers = this.teamMembers.filter(
        (teamMember) =>
          teamMember.member.familyName
            .toLowerCase()
            .includes(this.searchText.toLowerCase()) ||
          teamMember.teamRole.name
            .toLowerCase()
            .includes(this.searchText.toLowerCase()) ||
          (teamMember.member.email &&
            teamMember.member.email.includes(this.searchText.toLowerCase()))
      )
    }

    return orderBy(
      teamMembers,
      [this.sortField],
      this.sortOrder === 'none' ? undefined : this.sortOrder
    )
  }

  @action.bound
  setSort(sortField: TeamMemberTableColumns['key'], sortOrder: TableSortOrder) {
    this.sortField = sortField
    this.sortOrder = sortOrder
  }

  /**
   * Shows the invite modal
   */
  @action.bound
  showInviteModal() {
    this.inviteTeamMemberModalVM.show(null)
  }

  /**
   * Available team roles.
   */
  @computed
  get teamRoles() {
    return this.teamRoleStore.forWorkspace(Id.assert(this.team.workspace.id))
  }

  @computed
  get canManageTeamMember() {
    const currentMember = this.getCurrentMember()
    if (currentMember.hasPermission('ADMIN_TEAMS_VIEW')) {
      return true
    }

    const currentTeamMember = this.teamMembers.find(
      (m) => m.member.user.id === currentMember.user.id
    )

    return currentTeamMember?.hasPermission('MANAGE_TEAM_MEMBER') ?? false
  }

  /**
   * Changes the team member role.
   *
   * @param {*} teamMember
   * @param {*} newRoleId
   */
  changeTeamMemberRole = task.resolved(
    async (teamMember: TeamMember, newRoleId: string) => {
      if (!this.canManageTeamMember) {
        return
      }

      if (teamMember.teamRole.id === newRoleId) {
        return
      }

      const newRole = this.teamRoles.find((r) => r.id === newRoleId)
      const alert = this.newAlert().show()

      try {
        await this.teamMemberStore.updateTeamMember(
          {
            team_id: Id.assert(teamMember.team.id),
            user_id: Id.assert(teamMember.member.user.publicId),
          },
          {
            team_role_id: newRoleId,
          }
        )
        alert
          .withState(AlertState.Success)
          .saying(
            'Role Changed',
            `${teamMember.member.displayName} now has the ${
              newRole?.name ?? 'selected'
            } role`
          )
          .autoDismiss()
      } catch (err) {
        alert.fromError(err)
        console.error(err)
      }
    }
  )

  /**
   * Deactivates the team member
   *
   * @param {*} teamMember
   */
  removeTeamMember = task(async (teamMember: TeamMember) => {
    if (!(await this.removeConfirmDialog.show())) {
      return
    }
    try {
      await this.teamMemberStore.deleteTeamMember({
        team_id: Id.assert(teamMember.team.id),
        user_id: Id.assert(teamMember.member.user.publicId),
      })
    } catch (err) {
      this.newAlert().fromError(err).show()
      console.error(err)
    }
  })
}
